// src/pages/ComparisonProfile/components/BatchJobStatus.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Alert,
} from "reactstrap";
import { fetchJobStatus } from "../../../helpers/eirna_backend_helper";
import { toast } from "react-toastify";

const BatchJobStatus = ({ jobStatus, onUpdateJobStatus, projectId }) => {
  const [jobResult, setJobResult] = useState(null);
  const [plotData, setPlotData] = useState(null);
  const [error, setError] = useState(null);

  // Poll job status using the same pattern as subcodon
  const pollTaskStatus = useCallback(
    async (id) => {
      try {
        const response = await fetchJobStatus(id);
        const data = response?.data || response || {};

        if (["SUCCESS", "FAILURE"].includes(data.state)) {

          if (data.state === "SUCCESS") {
            if (data.result?.message === "user logged out") {
              return;
            }
            if (data.result?.message) {
              setError(data.result.message);
              toast.error(data.result.message);
              onUpdateJobStatus({
                ...jobStatus,
                status: "failed",
                message: data.result.message,
              });
            } else if (data.result) {
              setPlotData(data.result.plot || data.result);
              setJobResult(data.result);
              toast.success("Batch comparison job completed successfully");
              onUpdateJobStatus({
                ...jobStatus,
                status: "completed",
                message: "Job completed successfully",
              });
            }
          } else {
            const errorMsg = `Job failed. Task ID: ${id}`;
            setError(errorMsg);
            toast.error(errorMsg);
            onUpdateJobStatus({
              ...jobStatus,
              status: "failed",
              message: errorMsg,
            });
          }
        } else {
          // Continue polling
          setTimeout(() => pollTaskStatus(id), 2000);
        }
      } catch (err) {
        console.error("Polling error:", err);
        const errorMsg = `Unable to fetch job status. Task ID: ${id}`;
        setError(errorMsg);
        toast.error(errorMsg);
        onUpdateJobStatus({
          ...jobStatus,
          status: "failed",
          message: errorMsg,
        });
      }
    },
    [jobStatus, onUpdateJobStatus]
  );

  // Start polling when job is running
  useEffect(() => {
    if (jobStatus?.status === "running" && jobStatus.taskId) {
      pollTaskStatus(jobStatus.taskId);
    }
  }, [jobStatus, pollTaskStatus]);

  // Extract download link from the plot HTML
  const extractDownloadLink = (plotHtml) => {
    if (!plotHtml) return null;

    const parser = new DOMParser();
    const doc = parser.parseFromString(plotHtml, "text/html");
    const link = doc.querySelector("a[href]");

    if (link) {
      return {
        url: link.href,
        text: link.textContent.trim(),
      };
    }
    return null;
  };

  const handleDownload = (downloadUrl) => {
    window.open(downloadUrl, "_blank");
  };

  const getStatusIcon = () => {
    switch (jobStatus?.status) {
      case "completed":
        return "ri-check-circle-line";
      case "failed":
        return "ri-error-warning-line";
      case "running":
        return "ri-loader-4-line";
      default:
        return "ri-information-line";
    }
  };

  if (!jobStatus) return null;

  const downloadLink = plotData ? extractDownloadLink(plotData) : null;

  return (
    <Card>
      <CardHeader className="bg-light">
        <h5 className="mb-0 fw-semibold d-flex align-items-center">
          <i className="ri-task-line me-2 text-primary"></i>
          Batch Job Status
        </h5>
      </CardHeader>
      <CardBody className="py-3">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <i
              className={`${getStatusIcon()} me-2 ${
                jobStatus.status === "running" ? "rotate-animation" : ""
              }`}
              style={{
                fontSize: "18px",
                color:
                  jobStatus.status === "running"
                    ? "#3b82f6"
                    : jobStatus.status === "completed"
                    ? "#10b981"
                    : "#ef4444",
              }}
            ></i>
            <div>
              <div className="fw-semibold">
                {jobStatus.status === "running" &&
                  "Processing Batch Comparison"}
                {jobStatus.status === "completed" &&
                  "Batch Comparison Completed"}
                {jobStatus.status === "failed" && "Batch Comparison Failed"}
              </div>
              <small className="text-muted">Task ID: {jobStatus.taskId}</small>
            </div>
          </div>
        </div>


        {jobStatus.status === "failed" && error && (
          <Alert color="danger" className="py-2 px-3 mb-3">
            <small>
              <i className="ri-error-warning-line me-1"></i>
              {error}
            </small>
          </Alert>
        )}

        {jobStatus.status === "completed" && downloadLink && (
          <div className="text-center mb-3">
            <h6 className="text-success mb-2">Files Ready for Download</h6>
            <p className="text-muted mb-3">
              Your batch processing results are ready
            </p>
            <Button
              color="success"
              onClick={() => handleDownload(downloadLink.url)}
              className="d-flex align-items-center mx-auto"
              style={{ width: "fit-content" }}
            >
              <i className="ri-download-line me-2"></i>
              {downloadLink.text || "Download Files"}
            </Button>
          </div>
        )}

        {jobStatus.status === "completed" && jobResult?.short_code && (
          <Alert color="info" className="mb-3">
            <h6>Share this result using short code:</h6>
            <code>{jobResult.short_code}</code>
          </Alert>
        )}

        {jobStatus.status === "running" && (
          <Alert color="info" className="mb-0">
              Your batch comparison job is being processed.
          </Alert>
        )}
      </CardBody>

      <style jsx>{`
        .rotate-animation {
          animation: rotate 2s linear infinite;
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </Card>
  );
};

export default BatchJobStatus;
